import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Filter, X, Save, Trash2 } from "lucide-react";

import { activityStore } from "../../../../../store/activity.store";
import { Button } from "../../../../ui/button";
import { Combobox } from "../../../../ui/combobox";
import { Modal } from "../../../../ui/modal";
import { Input } from "../../../../ui/input";
import { Popconfirm } from "../../../../ui/popconfirm";
import { IntervalFilter } from "../../../../../model/activity.model";

const FiltersSelect = () => {
  const { formatMessage } = useIntl();
  const intervalFilters = activityStore(state => state.intervalFilters);
  const filtersConfiguration = activityStore(state => state.filtersConfiguration);
  const setFiltersConfiguration = activityStore(state => state.setFiltersConfiguration);
  const getIntervalsFilters = activityStore(state => state.getIntervalsFilters);
  const setIntervalsParams = activityStore(state => state.setIntervalsParams);
  const createFilter = activityStore(state => state.createFilter);
  const updateFilter = activityStore(state => state.updateFilter);
  const deleteFilter = activityStore(state => state.deleteFilter);
  const getIntervalsFiltersLoading = activityStore(state => state.loading.getIntervalsFilters);
  const createFilterLoading = activityStore(state => state.loading.createFilter);
  const { types, tags } = activityStore(state => state.intervalsParams);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [filterName, setFilterName] = useState('');

  const chosenFilter = useMemo(() =>
    intervalFilters?.find(filter => filter.name === filtersConfiguration),
    [intervalFilters, filtersConfiguration]
  );

  const hasFilters = !!types?.length || !!tags?.length;
  const hasSelectedFilter = !!filtersConfiguration;

  const isFilterUpdated = useMemo(() => {
    if (!chosenFilter) return false;
    return (chosenFilter.types && JSON.stringify(types?.sort()) !== JSON.stringify(chosenFilter.types.sort()))
      || (chosenFilter.tags && JSON.stringify(tags?.sort()) !== JSON.stringify(chosenFilter.tags.sort()));
  }, [chosenFilter, tags, types]);

  const clearFilterSelect = useCallback(() => {
    setFiltersConfiguration(null);
    setIntervalsParams({ tags: [], types: [] });
  }, [setFiltersConfiguration, setIntervalsParams]);

  const onFilterSelectChangeHandlerCallback = useCallback((value: string) => {
    const filtersToSet = intervalFilters?.find(filter => filter.name === value);

    if (filtersToSet) {
      setFiltersConfiguration(value);
      setIntervalsParams({ tags: filtersToSet.tags, types: filtersToSet.types });
    }
  }, [intervalFilters, setIntervalsParams, setFiltersConfiguration]);

  const createFilterHandler = useCallback(() => {
    const filter = {
      name: filterName,
      types,
      tags
    };

    void createFilter(filter).then(() => {
      setIsCreateModalOpen(false);
      setFilterName('');
      setFiltersConfiguration(filter.name);
    });
  }, [filterName, types, tags, createFilter, setFiltersConfiguration]);

  const updateFilterCallback = useCallback(() => {
    if (chosenFilter) {
      const newFilter: IntervalFilter = {
        ...chosenFilter,
        types,
        tags
      };
      void updateFilter(newFilter);
    }
  }, [chosenFilter, tags, types, updateFilter]);

  const deleteFilterCallback = useCallback(() => {
    if (chosenFilter) {
      void deleteFilter(chosenFilter.id);
      setIntervalsParams({ tags: [], types: [] });
      setFiltersConfiguration(null);
    }
  }, [chosenFilter, deleteFilter, setIntervalsParams, setFiltersConfiguration]);

  useEffect(() => {
    void getIntervalsFilters();
  }, []);

  const options = useMemo(() =>
    intervalFilters?.map(filter => ({
      value: filter.name,
      label: filter.name
    })) || [],
    [intervalFilters]
  );

  // Case 1: No types/tags selected AND no filter selected - show "or" + Select Filter
  if (!hasFilters && !hasSelectedFilter) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground text-sm">or</span>
        <Combobox
          className="w-[180px] bg-white dark:bg-muted shadow-sm border-border/30 dark:border-transparent"
          options={options}
          value={filtersConfiguration}
          onChange={onFilterSelectChangeHandlerCallback}
          loading={getIntervalsFiltersLoading}
          placeholder={formatMessage({ id: 'SELECT_FILTER' })}
          searchPlaceholder={formatMessage({ id: 'SEARCH' })}
          emptyText={formatMessage({ id: 'NO_RESULTS' })}
          icon={<Filter className="h-4 w-4 shrink-0 opacity-70" />}
        />
      </div>
    );
  }

  // Case 2: Types/tags selected AND no filter selected - show Save button
  if (hasFilters && !hasSelectedFilter) {
    return (
      <>
        <Button variant="segment" onClick={() => setIsCreateModalOpen(true)}>
          <Save className="h-4 w-4" />
          {formatMessage({ id: 'SAVE' })}
        </Button>
        <Modal
          title={formatMessage({ id: 'CREATE_FILTER_CONFIGURATION' })}
          onCancel={() => setIsCreateModalOpen(false)}
          open={isCreateModalOpen}
          onOk={createFilterHandler}
          okText={formatMessage({ id: 'SAVE' })}
          confirmLoading={createFilterLoading}
        >
          <Input
            value={filterName}
            placeholder={formatMessage({ id: 'FILTER_NAME_PLACEHOLDER' })}
            onChange={(e) => setFilterName(e.target.value)}
          />
        </Modal>
      </>
    );
  }

  // Case 3: Filter is selected - show filter chip with X, delete button, and Save button if modified
  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-white shadow-sm border border-border/50 dark:bg-muted dark:border-transparent text-sm">
        <Filter className="h-3.5 w-3.5 opacity-70" />
        <span>{filtersConfiguration}</span>
        <button
          onClick={clearFilterSelect}
          className="ml-1 rounded-sm opacity-70 hover:opacity-100 hover:bg-muted"
          title={formatMessage({ id: 'NONE' })}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <Popconfirm
        title={formatMessage({ id: 'CONFIRM_FILTER_DELETE' })}
        onConfirm={deleteFilterCallback}
      >
        <Button variant="segment" size="icon" className="h-8 w-8">
          <Trash2 className="h-4 w-4" />
        </Button>
      </Popconfirm>
      {isFilterUpdated && (
        <Button variant="segment" size="sm" onClick={updateFilterCallback}>
          <Save className="h-4 w-4" />
          {formatMessage({ id: 'SAVE' })}
        </Button>
      )}
    </div>
  );
};

export default FiltersSelect;
